import type {
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes'

/**
 * Submitting an empty string as a `valueString` even in an optional field yields errors
 * on Medplum's server. The QuestionnaireForm component will submit empty strings for any
 * field that has been touched but not filled out. This is a problem for optional fields
 * which look like they are not filled out but are actually filled out with an empty string.
 *
 * This function removes empty string answers from a given QuestionnaireResponse
 * to prevent these errors. It returns a new QuestionnaireResponse object with the
 * empty string answers removed.
 * @param questionnaireResponse - The QuestionnaireResponse to update.
 * @returns The updated QuestionnaireResponse object with empty string answers removed.
 */
export function removeEmptyAnswersFromQuestionnaireResponse(
  questionnaireResponse: QuestionnaireResponse
): QuestionnaireResponse {
  const updatedResponse: QuestionnaireResponse = {
    ...questionnaireResponse,
    item: questionnaireResponse.item?.map((item) =>
      removeEmptyAnswersFromResponseItem(item)
    ),
  }

  return updatedResponse
}

function removeEmptyAnswersFromResponseItem(
  rawItem: QuestionnaireResponseItem
): QuestionnaireResponseItem {
  const updatedItem: QuestionnaireResponseItem = {
    ...rawItem,
  }

  if (updatedItem.item != null) {
    // recursively remove empty string answers from child items
    updatedItem.item = updatedItem.item?.map((childItem) =>
      removeEmptyAnswersFromResponseItem(childItem)
    )
  } else {
    // remove empty answers from this item
    updatedItem.answer = updatedItem.answer?.filter((answer) => {
      const answerKeys = Object.keys(
        answer
      ) as (keyof QuestionnaireResponseItemAnswer)[]

      // every answer should have one and only one answer field
      if (answerKeys.length !== 1) {
        return false
      }

      // =================
      // type-based switch
      // =================
      // we should also check that the one answer field is present and not undefined.
      // some answer types may want to refine this check further; for example, if the answer
      // is a `valueString`, it should not be an empty string
      switch (answerKeys[0]) {
        case 'valueString':
          // if `valueString` is present, it should not be an empty string or contain only whitespace
          return answer.valueString != null && answer.valueString.trim() !== ''
        default:
          // all remaining answer types are considered valid as long as they are not null or undefined
          return answer[answerKeys[0]] != null
      }
    })
  }

  return updatedItem
}
